import { SlCheckbox, SlIcon, SlSpinner } from '@shoelace-style/shoelace/dist/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination } from '../Pagination';
import { getNestedValue } from '../utils';
import { SubRowProps } from './SubRow';
import TableBodyRow from './TableBodyRow';

export interface ColumnData<T extends Record<string, any> = {}> {
  header: string;
  field: string;
  id: string;
  sort?: boolean;
  width?: number;
  renderer?: (data: T) => React.ReactNode;
  headerRenderer?: (data: Pick<ColumnData<T>, 'field' | 'sort' | 'header' | 'id'>) => React.ReactNode;
  show?: boolean;
}

export interface TableProps<T> {
  data: Array<T & { subRows?: Array<T> }>;
  columns: Array<ColumnData<T>>;
  rowSelection?: boolean;
  onSelectionChange?: (data: Array<T>) => void;
  noDataText: string;
  rowsPerPage?: number;
  variant?: 'filled' | 'outlined';
  expandable?: boolean;
  subrowProps?: SubRowProps;
  /** Caller sorts and pages: `data` is one ready-made page, rendered as-is. */
  serverSide?: boolean;
  /** Row count across all pages. Required with `serverSide`. */
  totalRows?: number;
  /** `null` means sorting was cleared. */
  onSortChange?: (sort: { field: string; direction: 'asc' | 'desc' } | null) => void;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  /**
   * Show a loading row in place of the rows, keeping the header and pager. Meant
   * for server-side tables, where hiding the whole table on every page or sort
   * change loses the pager's state and makes the layout jump.
   */
  loading?: boolean;
  loadingText?: string;
  /**
   * Controlled page. Server-side callers already own the page - passing it here
   * keeps the pager in step when they reset it, e.g. after a sort.
   */
  page?: number;
}

const uuid = () => Math.random().toString(16).slice(-8);

const TableComponent = <T extends Record<string, any>>(props: TableProps<T>) => {
  const locale = localStorage.getItem('co4:app/language') ?? 'de';
  const [t] = useTranslation();
  const {
    data: ogData,
    columns,
    rowSelection = false,
    onSelectionChange = () => {},
    noDataText = t('noRecordsFound'),
    rowsPerPage = 10,
    variant = 'outlined',
    expandable = false,
    serverSide = false,
    totalRows,
    onSortChange,
    onPageChange: onPageChangeProp,
    onRowsPerPageChange,
    loading = false,
    loadingText,
    page: pageProp,
  } = props;
  const [selection, setSelection] = React.useState<Array<string>>([]);
  const [sort, setSort] = React.useState({ field: '', order: '' });
  const [originalData, setOriginalData] = React.useState<Array<T & { selectionId?: string }>>([]);
  const [pagination, setPagination] = React.useState({
    currentPage: 1,
    rowsPerPage,
    totalPages: 1,
  });

  const data: Array<T & { selectionId?: string }> = React.useMemo(() => {
    // setPagination(({ rowsPerPage }) => ({
    //   currentPage: 1,
    //   rowsPerPage,
    //   totalPages: Math.ceil(ogData.length / rowsPerPage),
    // }));

    setPagination((prev) => {
      // Server-side: ogData is one page, so page off the server's total.
      const rowCount = serverSide ? totalRows ?? ogData.length : ogData.length;
      const totalPages = Math.ceil(rowCount / rowsPerPage);
      const currentPage = totalPages === 0 ? 1 : Math.max(1, Math.min(prev.currentPage, totalPages));
      return { ...prev, rowsPerPage, totalPages, currentPage };
    });

    const dataWithIds = ogData.map((d) => ({ ...d, selectionId: uuid() }));
    setOriginalData(dataWithIds);
    return dataWithIds;
  }, [ogData, serverSide, totalRows]);

  const activePage = pageProp ?? pagination.currentPage;

  const currentRange = React.useMemo(() => {
    const start = (activePage - 1) * pagination.rowsPerPage;
    // Server-side: `data` is the page itself, so it ends where the page ends.
    if (serverSide) return { start, end: start + data.length };
    const end = start + pagination.rowsPerPage > data.length ? data.length : start + pagination.rowsPerPage;
    return { start, end };
  }, [pagination, data.length, serverSide, activePage]);

  const sortBy = React.useCallback(
    (a, b) => {
      const { field, order } = sort;
      if (!field || !order) return 0;

      const column = columns.find((col) => col.field === field);
      if (!column) return 0;

      // If the column has a custom renderer, we need to handle it differently
      if (column.renderer) {
        const valA = column.renderer(a);
        const valB = column.renderer(b);

        // Handle React elements by extracting data-value or actual data
        if (React.isValidElement(valA) || React.isValidElement(valB)) {
          // Try to get the data-value attribute if it exists
          const getDataValue = (element: React.ReactElement) => {
            if (element.props['data-value']) return element.props['data-value'];
            if (element.props['data-production'] !== undefined) return element.props['data-production'];
            return element.props.children;
          };

          const finalValA = React.isValidElement(valA) ? getDataValue(valA) : valA;
          const finalValB = React.isValidElement(valB) ? getDataValue(valB) : valB;

          if (typeof finalValA === 'string' && typeof finalValB === 'string') {
            return order === 'asc'
              ? finalValA.localeCompare(finalValB, locale)
              : finalValB.localeCompare(finalValA, locale);
          }

          if (typeof finalValA === 'number' && typeof finalValB === 'number') {
            return order === 'asc' ? finalValA - finalValB : finalValB - finalValA;
          }

          if (typeof finalValA === 'boolean' && typeof finalValB === 'boolean') {
            return order === 'asc'
              ? finalValA === finalValB
                ? 0
                : finalValA
                ? -1
                : 1
              : finalValA === finalValB
              ? 0
              : finalValA
              ? 1
              : -1;
          }

          return 0;
        }

        // Handle string/number values from renderers
        if (typeof valA === 'string' && typeof valB === 'string') {
          return order === 'asc' ? valA.localeCompare(valB, locale) : valB.localeCompare(valA, locale);
        }

        if (typeof valA === 'number' && typeof valB === 'number') {
          return order === 'asc' ? valA - valB : valB - valA;
        }

        return 0;
      }

      // For regular fields, handle nested paths
      const valA = getNestedValue(a, field);
      const valB = getNestedValue(b, field);

      // Handle arrays (like details.name[0].value)
      const finalValA = Array.isArray(valA) ? valA[0]?.value ?? valA : valA;
      const finalValB = Array.isArray(valB) ? valB[0]?.value ?? valB : valB;

      const multiplier = order === 'asc' ? 1 : -1;

      if (typeof finalValA === 'number' && typeof finalValB === 'number') {
        return order === 'asc' ? finalValA - finalValB : finalValB - finalValA;
      } else if (typeof finalValA === 'string' && typeof finalValB === 'string') {
        return multiplier * finalValA.localeCompare(finalValB, locale);
      } else return 0;
    },
    [sort, locale, columns],
  );

  const currentPageData = React.useMemo(() => {
    // Already sorted and paged by the caller.
    if (serverSide) return data;
    if (!sort.field || !sort.order) {
      // If no sorting is applied, use the original data order
      return originalData.slice(currentRange.start, currentRange.end);
    }
    // Apply sorting when sort is active
    return [...data].sort(sortBy).slice(currentRange.start, currentRange.end);
  }, [data, sortBy, currentRange, sort, originalData, serverSide]);

  const handleHeaderClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const target = e.target as HTMLElement;
      const headerElement = target.closest('[id]');
      const field = headerElement?.id;
      if (!field) return;

      // Same cycle in both modes - asc, desc, cleared - server-side just reports
      // it instead of sorting in place.
      let next: { field: string; order: string };
      if (field === sort.field) {
        if (sort.order === 'asc') {
          next = { field, order: 'desc' };
        } else if (sort.order === 'desc') {
          next = { field: '', order: '' };
        } else {
          next = { field, order: 'asc' };
        }
      } else {
        // If clicking on a different field, start with ascending sort
        next = { field, order: 'asc' };
      }
      setSort(next);
      onSortChange?.(next.order ? { field: next.field, direction: next.order as 'asc' | 'desc' } : null);
    },
    [sort, onSortChange],
  );

  const handleSelectionChange = (id: string | null, all?: boolean) => {
    let updatedSelections = [...selection];

    if (all !== undefined) {
      updatedSelections = all ? currentPageData.map((d) => d.selectionId!) : [];
    } else if (id !== null) {
      if (updatedSelections.includes(id)) {
        updatedSelections.splice(updatedSelections.indexOf(id), 1);
      } else {
        updatedSelections.push(id);
      }
    }

    const selectionData = all
      ? [...data]
      : updatedSelections.map((sel) => data.find((d) => d.selectionId === sel)!).filter(Boolean);

    onSelectionChange(selectionData as T[]);
    setSelection(updatedSelections);
  };

  let areAllSelected =
    rowSelection && selection.length !== 0 && currentPageData.every((d) => selection.includes(d.selectionId!));

  const onPageChange = (currentPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage }));
    onPageChangeProp?.(currentPage);
  };

  const onRowsChange = (rowsPerPage: number) => {
    const rowCount = serverSide ? totalRows ?? data.length : data.length;
    setPagination({
      currentPage: 1,
      rowsPerPage,
      totalPages: Math.ceil(rowCount / rowsPerPage),
    });
    onRowsPerPageChange?.(rowsPerPage);
  };

  return (
    <>
      <div className={`table-wrapper ${variant}`}>
        <table>
          <thead>
            <tr>
              {rowSelection && (
                <th id="select-all-box">
                  <div id="select-all" className="head-col selection-box ">
                    <SlCheckbox
                      disabled={!data.length}
                      checked={areAllSelected}
                      onSlChange={() => {
                        handleSelectionChange(null, !areAllSelected);
                      }}></SlCheckbox>
                  </div>
                </th>
              )}
              {expandable && <th className="expand-button-td"></th>}

              {columns.map((col) => {
                const { id, field, header, headerRenderer, width = 'auto', sort: colSort = true, show } = col;
                let icon = 'list';
                if (sort.field === field) {
                  icon = sort.order === 'desc' ? 'sort-alpha-up-alt' : 'sort-alpha-down';
                }

                if (show === false) return null;

                return (
                  <th
                    key={id}
                    id={field}
                    style={{ width }}
                    onClick={(event) => {
                      if (colSort) {
                        handleHeaderClick(event);
                      }
                    }}>
                    {headerRenderer ? (
                      headerRenderer({ field, header, id, sort: colSort })
                    ) : (
                      <div id={field} className="head-col" data-value={header}>
                        {header}
                        {colSort && <SlIcon name={icon}></SlIcon>}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="table-loading-row">
                <td colSpan={columns.length + +rowSelection}>
                  <SlSpinner />
                  <span>{loadingText ?? t('loading')}</span>
                </td>
              </tr>
            ) : data.length > 0 ? (
              currentPageData.map((item, index) => (
                <TableBodyRow
                  key={`table-body-row-${item?.id}-${item?.selectionId}`}
                  expandable={expandable}
                  rowSelection={rowSelection}
                  selected={selection.includes(item.selectionId!)}
                  onCheckboxToggle={handleSelectionChange}
                  columns={columns}
                  subrowProps={props?.subrowProps ?? ({} as SubRowProps)}
                  index={index}
                  data={item}
                />
              ))
            ) : (
              <tr>
                <td colSpan={columns.length + +rowSelection}>{noDataText}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {(pagination.totalPages > 1 || pagination.rowsPerPage !== rowsPerPage) && (
        <Pagination
          currentRange={currentRange}
          totalData={serverSide ? totalRows ?? data.length : data.length}
          totalPages={pagination.totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={onPageChange}
          onRowsChange={onRowsChange}
          activePage={activePage}
        />
      )}
    </>
  );
};

export const Table = React.memo(TableComponent) as typeof TableComponent;
